import { TypedDocumentNode } from '@apollo/client';
import { Client, createClient } from 'graphql-sse';
import { FunctionComponent, PropsWithChildren } from 'react';

import { APIConfiguration } from '@savgroup-front-common/configuration';
import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';

import { createGenericContext } from '../../helpers';
import { getGqlString } from '../../helpers/getGqlString';

export interface GraphQlSseContextArgs {
  graphqlSseClient: Client;
  onSubscribe: (args: {
    subscriptionKey: string;
    query: TypedDocumentNode<any, any>;
    variables?: any;
    onNext: (data: any) => void;
    operationName: string;
  }) => void;
  onUnsubscribe: (subscriptionKey: string) => void;
}

const [useGraphQlSseContext, CreateGraphQlSseProvider] =
  createGenericContext<GraphQlSseContextArgs>();

const client = createClient({
  url: (payload) => {
    const url = new URL(APIConfiguration.graphQlGateway);

    if (payload.operationName) {
      url.searchParams.set('operationName', payload.operationName);
    }

    return url.toString();
  },
});

const state: Record<
  string,
  | {
      subscription: () => void;
      operationName: string;
      callbacks: ((data: any) => void)[];
    }
  | undefined
> = {};

const GraphQlSseContextProvider: FunctionComponent<
  PropsWithChildren<Record<never, never>>
> = ({ children }) => {
  const handleSubscribe = ({
    subscriptionKey,
    query,
    variables = {},
    onNext,
    operationName,
  }: {
    subscriptionKey: string;
    query: TypedDocumentNode<any, any>;
    variables?: any;
    onNext: (data: any) => void;
    operationName: string;
  }) => {
    const existingSubscription = state[subscriptionKey];

    if (existingSubscription) {
      state[subscriptionKey] = {
        ...existingSubscription,
        callbacks: [...existingSubscription.callbacks, onNext],
      };

      return;
    }

    const finalQuery = getGqlString(query) ?? '';

    const subscription = client.subscribe(
      {
        query: finalQuery,
        variables,
        operationName,
      },
      {
        next: (result: { data: any }) => {
          const existingSubscription = state[subscriptionKey];

          for (const callback of existingSubscription?.callbacks ?? []) {
            callback(result.data);
          }
        },
        error: (error: Error) => {
          logError(error);
        },
        complete: () => {
          return undefined;
        },
      },
    );

    state[subscriptionKey] = {
      subscription,
      operationName,
      callbacks: [onNext],
    };

    return;
  };

  const handleUnsubscribe = (subscriptionKey: string) => {
    const existingSubscription = state[subscriptionKey];

    if (existingSubscription?.subscription) {
      existingSubscription.subscription();

      state[subscriptionKey] = undefined;
    }
  };

  return (
    <CreateGraphQlSseProvider
      value={{
        graphqlSseClient: client,
        onSubscribe: handleSubscribe,
        onUnsubscribe: handleUnsubscribe,
      }}
    >
      {children}
    </CreateGraphQlSseProvider>
  );
};

export { GraphQlSseContextProvider, useGraphQlSseContext };
